// DVI レンダラの Worker クライアント (C3)。
//
// wasm・文書・フォント・ラスタライズは dviWorker.ts (Web Worker) が持ち、
// メインスレッドはこの非同期プロキシ経由で操作する。render の結果は
// Transferable なピクセルバッファで受け取り、putImageData するだけになる
// (メインスレッドのブロッキングを解消する)。

import type { PageLayoutMode } from './loader';

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./dviWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (
      ev: MessageEvent<{ id: number; ok: boolean; result?: unknown; error?: string }>,
    ) => {
      const p = pending.get(ev.data.id);
      if (!p) {
        return;
      }
      pending.delete(ev.data.id);
      if (ev.data.ok) {
        p.resolve(ev.data.result);
      } else {
        p.reject(new Error(ev.data.error ?? 'worker error'));
      }
    };
    worker.onerror = (e) => {
      const err = new Error(`dvi worker error: ${e.message}`);
      for (const p of pending.values()) {
        p.reject(err);
      }
      pending.clear();
    };
  }
  return worker;
}

function call<T>(method: string, args: unknown[]): Promise<T> {
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    getWorker().postMessage({ id, method, args });
  });
}

export interface PageMetricsSp {
  widthSp: number;
  heightSp: number;
  depthSp: number;
}

export interface PageLinkSp {
  x: number;
  y: number;
  w: number;
  h: number;
  href: string;
}

/** テキストラン (C2)。x/y = 位置 (sp、y はベースライン)、s = サイズ、w = 送り幅 */
export interface PageTextRun {
  x: number;
  y: number;
  s: number;
  w: number;
  t: string;
}

/** Worker のウォームアップ: wasm の取得・コンパイルとフォントの先読み。 */
export function warmupRenderer(fontUrls: string[]): Promise<void> {
  return call('warmup', [fontUrls]);
}

/** Worker 内の DviDocument への非同期プロキシ。 */
export class WorkerDviDocument {
  private constructor(
    private handle: number,
    public readonly pageCount: number,
  ) {}

  /** DVI を取得して Worker 内で開く (フォント解決はしない)。 */
  static async open(url: string): Promise<WorkerDviDocument> {
    const r = await call<{ handle: number; pageCount: number }>('open', [url]);
    return new WorkerDviDocument(r.handle, r.pageCount);
  }

  resolveFonts(kind?: 'face' | 'metrics'): Promise<void> {
    return call('resolveFonts', [this.handle, kind]);
  }

  pageSizeSp(
    mode: PageLayoutMode,
    marginPt: number,
  ): Promise<{ widthSp: number; heightSp: number }> {
    return call('pageSizeSp', [this.handle, mode, marginPt]);
  }

  pageMetricsSp(page: number, mode: PageLayoutMode, marginPt: number): Promise<PageMetricsSp> {
    return call('pageMetricsSp', [this.handle, page, mode, marginPt]);
  }

  pageLinks(page: number, mode: PageLayoutMode, marginPt: number): Promise<PageLinkSp[]> {
    return call('pageLinks', [this.handle, page, mode, marginPt]);
  }

  pageText(page: number, mode: PageLayoutMode, marginPt: number): Promise<PageTextRun[]> {
    return call('pageText', [this.handle, page, mode, marginPt]);
  }

  async renderPage(
    page: number,
    pxPerPt: number,
    mode: PageLayoutMode,
    marginPt: number,
    fg: number,
  ): Promise<ImageData> {
    const r = await call<{ width: number; height: number; buffer: ArrayBuffer }>('render', [
      this.handle,
      page,
      pxPerPt,
      mode,
      marginPt,
      fg,
    ]);
    return new ImageData(new Uint8ClampedArray(r.buffer), r.width, r.height);
  }

  close(): void {
    void call('close', [this.handle]);
  }
}
